export interface ScriptFile {
  id: string;
  name: string;
  path: string;
  content: string;
  language: 'typescript' | 'javascript';
  isDirty: boolean;
  lastSaved: Date | null;
  cursorPosition: { line: number; column: number };
  scrollPosition: { top: number; left: number };
}

export interface FileSystemNode {
  id: string;
  name: string;
  path: string;
  type: 'file' | 'folder';
  children?: string[]; // IDs of child nodes (for folders)
  parentId: string | null;
  language?: 'typescript' | 'javascript'; // For files only
}

export interface OpenTab {
  fileId: string;
  isPinned: boolean;
  isPreview: boolean; // Single-click opens in preview mode
}

export interface DiagnosticMessage {
  id: string;
  fileId: string;
  filePath: string;
  severity: 'error' | 'warning' | 'info' | 'hint';
  message: string;
  line: number;
  column: number;
  endLine: number;
  endColumn: number;
  code?: string | number;
  source: string; // 'typescript' | 'eslint' | etc.
}

export interface CompilationResult {
  success: boolean;
  outputFiles: OutputFile[];
  diagnostics: DiagnosticMessage[];
  duration: number;
}

export interface OutputFile {
  path: string;
  content: string;
}

export interface EditorSettings {
  theme: 'vs-dark' | 'vs-light' | 'hc-black';
  fontSize: number;
  fontFamily: string;
  tabSize: number;
  insertSpaces: boolean;
  wordWrap: 'off' | 'on' | 'wordWrapColumn' | 'bounded';
  minimap: boolean;
  lineNumbers: 'on' | 'off' | 'relative' | 'interval';
  autoSave: boolean;
  autoSaveDelay: number; // ms
  formatOnSave: boolean;
  formatOnPaste: boolean;
  autoCloseBrackets: boolean;
  autoIndent: boolean;
}

export interface SearchResult {
  fileId: string;
  filePath: string;
  line: number;
  column: number;
  matchLength: number;
  lineContent: string;
}

export type SidebarTab = 'explorer' | 'search' | 'problems' | 'debug';
export type BottomTab = 'problems' | 'output' | 'debug-console' | 'terminal';

export interface ScriptEditorState {
  // Files
  files: Record<string, ScriptFile>;
  openTabs: OpenTab[];
  activeTabId: string | null;
  loadingFiles: Set<string>; // Track files currently being loaded to prevent race conditions

  // File system tree
  fileTree: Record<string, FileSystemNode>;
  rootFolderId: string | null;

  // Sidebar
  sidebarVisible: boolean;
  sidebarWidth: number;
  activeSidebarTab: SidebarTab;

  // Bottom panel
  bottomPanelVisible: boolean;
  bottomPanelHeight: number;
  activeBottomTab: BottomTab;

  // Diagnostics
  diagnostics: DiagnosticMessage[];
  diagnosticCounts: {
    errors: number;
    warnings: number;
    infos: number;
  };

  // Compilation
  isCompiling: boolean;
  lastCompilationResult: CompilationResult | null;
  autoCompile: boolean;

  // Search
  searchQuery: string;
  searchResults: SearchResult[];
  isSearching: boolean;
  searchOptions: {
    caseSensitive: boolean;
    wholeWord: boolean;
    useRegex: boolean;
  };

  // Output
  outputMessages: OutputMessage[];

  // Settings
  settings: EditorSettings;
}

export interface OutputMessage {
  id: string;
  timestamp: Date;
  type: 'info' | 'warning' | 'error' | 'success';
  message: string;
  source: string;
}

export interface ScriptEditorActions {
  // File operations
  openFile: (filePath: string) => Promise<void>;
  closeFile: (fileId: string) => void;
  closeAllFiles: () => void;
  closeOtherFiles: (fileId: string) => void;
  saveFile: (fileId: string) => Promise<void>;
  saveAllFiles: () => Promise<void>;
  createFile: (path: string, language: 'typescript' | 'javascript') => Promise<string>;
  deleteFile: (fileId: string) => Promise<void>;
  renameFile: (fileId: string, newName: string) => Promise<void>;

  // File system operations
  createFolder: (parentPath: string, name: string) => Promise<string>;
  renameNode: (nodeId: string, newName: string) => Promise<void>;
  deleteNode: (nodeId: string) => Promise<void>;
  moveNode: (nodeId: string, newParentId: string) => Promise<void>;
  getNodeChildren: (nodeId: string) => FileSystemNode[];

  // Tab operations
  setActiveTab: (fileId: string) => void;
  reorderTabs: (fromIndex: number, toIndex: number) => void;
  pinTab: (fileId: string) => void;
  unpinTab: (fileId: string) => void;

  // Content operations
  updateFileContent: (fileId: string, content: string) => void;
  updateCursorPosition: (fileId: string, line: number, column: number) => void;
  updateScrollPosition: (fileId: string, top: number, left: number) => void;

  // Sidebar
  toggleSidebar: () => void;
  setSidebarWidth: (width: number) => void;
  setActiveSidebarTab: (tab: SidebarTab) => void;

  // Bottom panel
  toggleBottomPanel: () => void;
  setBottomPanelHeight: (height: number) => void;
  setActiveBottomTab: (tab: BottomTab) => void;

  // Compilation
  compile: () => Promise<CompilationResult>;
  setAutoCompile: (enabled: boolean) => void;
  clearDiagnostics: () => void;

  // Search
  search: (query: string) => void;
  setSearchOptions: (options: Partial<ScriptEditorState['searchOptions']>) => void;
  clearSearch: () => void;
  goToSearchResult: (result: SearchResult) => void;

  // Output
  addOutputMessage: (message: Omit<OutputMessage, 'id' | 'timestamp'>) => void;
  clearOutput: () => void;

  // Settings
  updateSettings: (settings: Partial<EditorSettings>) => void;
}

export const DEFAULT_EDITOR_SETTINGS: EditorSettings = {
  theme: 'vs-dark',
  fontSize: 14,
  fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
  tabSize: 2,
  insertSpaces: true,
  wordWrap: 'on',
  minimap: true,
  lineNumbers: 'on',
  autoSave: true,
  autoSaveDelay: 1000,
  formatOnSave: true,
  formatOnPaste: true,
  autoCloseBrackets: true,
  autoIndent: true,
};
